/**
 * A horizontal proportion bar with its value beside it.
 *
 * Three primitives share this shape in the prototype and differ only in
 * proportions and colour: `.meter` (a match score, 80px track), `.score` (a
 * scorecard criterion, 104px track) and `.hbar` (a chart row, flexible track).
 * They are one component with a `size` rather than three, because the drift
 * between three near-identical copies is exactly the kind of thing that makes
 * an interface look hand-assembled.
 *
 * **The value is always rendered as text.** PRD §9.7 forbids conveying
 * information by visual means alone, and a bar without its number does exactly
 * that — worse here than with colour, because a bar's length is unreadable to a
 * screen reader and hard to estimate by eye.
 *
 * The `<progress>`-style ARIA lives on a plain `div` rather than on a real
 * `<progress>` element: `<progress>` cannot be styled to the prototype's track
 * and fill across browsers, and `role="progressbar"` conveys the same thing.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.5.8, PRD §9.7
 */
import { cn } from '../../lib/cn';

/** Track widths, from the three prototype variants. */
const TRACK_SIZE = {
  /** `.meter` — a match or suitability score. */
  sm: 'w-20',
  /** `.score` — a scorecard criterion. */
  md: 'w-[104px]',
  /** `.hbar` — a chart row that fills the space it is given. */
  full: 'flex-1',
} as const;

/** Bar heights, matched to the track sizes above. */
const TRACK_HEIGHT = {
  sm: 'h-1.5',
  md: 'h-1.5',
  full: 'h-3.5',
} as const;

export type MeterSize = keyof typeof TRACK_SIZE;

export interface MeterProps {
  /** Current value, in the same unit as `max`. */
  value: number;
  /** The value representing a full bar. Must be greater than zero. */
  max: number;
  /**
   * The accessible name — what this bar measures. Required: a progressbar with
   * no name tells a screen-reader user a percentage and nothing else.
   */
  label: string;
  /**
   * The text shown beside the bar. Defaults to `value`, but a caller with a
   * unit or a formatted figure passes it through `lib/format` and supplies it
   * here.
   */
  display?: string | undefined;
  size?: MeterSize;
  /**
   * Fill colour as a Tailwind class. Defaults to blue — the interactive and
   * neutral data colour. Green is passed explicitly only where the bar's value
   * is the point of the element (§4.2).
   */
  fillClassName?: string | undefined;
  className?: string;
}

export function Meter({
  value,
  max,
  label,
  display,
  size = 'md',
  fillClassName = 'bg-blue',
  className,
}: MeterProps): React.JSX.Element {
  /* Clamped, not trusted. An actual above target is normal in a KPI table, and
     a fill wider than its track would overflow the rounded mask on some
     browsers rather than being clipped by it. */
  const pct = Math.min(100, Math.max(0, (value / max) * 100));

  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        className={cn(
          'bg-gray-100 overflow-hidden rounded-full',
          TRACK_SIZE[size],
          TRACK_HEIGHT[size],
          size !== 'full' && 'flex-none',
        )}
      >
        <span
          className={cn('block h-full rounded-full', fillClassName)}
          style={{ width: `${String(pct)}%` }}
        />
      </div>
      <span
        className={cn(
          'font-display flex-none text-right font-extrabold tabular-nums',
          size === 'full' ? 'text-navy-text w-[46px] text-[13px]' : 'w-7 text-sm',
          size === 'sm' && 'text-green-dark',
          size === 'md' && 'text-navy-text',
        )}
      >
        {display ?? value}
      </span>
    </div>
  );
}
